import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useRomFooter } from "../context/RomFooterContext";
import { endpoints } from "../lib/api";
import {
    getAll, getOne, put, queueSync, STORES, kvGet, kvSet,
} from "../lib/db";
import { computeROM, formatCurrency, scaleFromPropertyType } from "../lib/romEngine";
import { computeTM, buildEmptyTM, computeCleaning, buildEmptyCleaning } from "../lib/tmEngine";
import {
    CostTracker, LabourModal, ConsumablesModal, EquipmentModal,
    SubcontractorsModal, MiscModal, StaffingModal,
} from "../components/TMPanels";
import ClientInlineModal from "../components/ClientInlineModal";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { InvoiceImportModal } from "../components/ImportModals";
import { VarianceAnalysis } from "../components/VarianceAnalysis";
import {
    Calculator as CalcIcon, FloppyDisk, Printer, Plus, ArrowLeft, ArrowsLeftRight, X, FilePdf, Stack, ChartBar, Sparkle,
    PaperPlaneTilt, CaretDown, FolderPlus, CheckCircle, CircleNotch, ChatCircleText, House, Buildings,
} from "@phosphor-icons/react";
import { SendProposalModal } from "./calculator/SendProposalModal";
import { BLANK, hasMinimumInputs, makeRef, haversineMiles, isPrimeAreaLocation } from "./calculator/helpers";
import { Section, Field, PillGroup, Toggle, FormulaInput, CollapsibleSection } from "./calculator/primitives";
import { RoomBuildUp } from "./calculator/RoomBuildUp";
import { SiteConditions } from "./calculator/SiteConditions";
import { TotalCard, EmptyTotalCard } from "./calculator/TotalCard";
import { DeliverablesBlock } from "./calculator/DeliverablesBlock";
import { AIEstimator } from "./calculator/AIEstimator";
import { PrintROM } from "./calculator/PrintROM";
import { CleaningPanel, CleaningTotalCard } from "./calculator/CleaningPanel";
import { RateBasisBanner } from "./calculator/RateBasisBanner";

export default function Calculator() {
    const { user, online } = useAuth();
    const { setFooter } = useRomFooter();
    const navigate = useNavigate();
    const { jobId } = useParams();
    const editingJobId = jobId && jobId !== "new" ? jobId : null;
    const [searchParams] = useSearchParams();
    const scaleParam = searchParams.get("scale");
    const projectParam = searchParams.get("project");

    const [schedule, setSchedule] = useState(null);
    const [scheduleLoading, setScheduleLoading] = useState(true);
    const [inputs, setInputs] = useState(BLANK);
    const [clients, setClients] = useState([]);
    const [franchiseHQ, setFranchiseHQ] = useState(null);
    const [vendorStats, setVendorStats] = useState({});
    const [calibBands, setCalibBands] = useState(null);
    const [savedRef, setSavedRef] = useState(null);
    const [approval, setApproval] = useState(null);
    const [changeRequest, setChangeRequest] = useState(null);
    const [saveMsg, setSaveMsg] = useState("");
    const [exporting, setExporting] = useState(false);
    const pdfRef = useRef(null);
    const [ref, setRef] = useState(() => makeRef());
    const [projectLink, setProjectLink] = useState(null);
    const loadedJobRef = useRef(null);
    const didResyncRef = useRef(false);
    const [jobReady, setJobReady] = useState(false);

    /* Loads the active rate schedule (network → fallback to IDB cache).
     * Extracted so both the mount effect and the "Retry" button (shown when
     * the device has no cached schedule at all) can call it. */
    async function loadSchedule() {
        setScheduleLoading(true);
        let sch = null;
        try {
            const { data } = await endpoints.activeRateSchedule();
            sch = data;
            await put(STORES.rate_schedules, data);
            await kvSet("active_schedule_id", data.id);
        } catch {
            try {
                const all = await getAll(STORES.rate_schedules);
                sch = all.find((s) => s.is_active) || all[0] || null;
            } catch {
                sch = null;
            }
        } finally {
            setSchedule(sch);
            setScheduleLoading(false);
        }
    }

    /* Load the rate schedule ONCE per page mount — intentionally NOT tied to
     * editingJobId/scaleParam. It's a global resource, not job-specific;
     * re-running it on every job-id change (e.g. right after the AI
     * Estimator's first-message autosave swaps /calculator/new -> /calculator/{id})
     * would flip scheduleLoading back to true and hit the "Loading rate
     * schedule…" early-return below, unmounting the whole tree (incl.
     * AIEstimator, wiping its in-progress chat) for no reason — the schedule
     * itself hasn't changed. */
    useEffect(() => {
        loadSchedule();
    }, []);

    /* Load clients + franchise settings + vendor stats + calib bands + the
     * job being edited (network → fallback to IDB cache). These 4 feed OTHER
     * UI (client dropdown, prime-area detection, vendor stats, calibration
     * band) — not required to start chatting/dictating on a brand-new
     * estimate, so they run in parallel and never block `jobReady`. */
    useEffect(() => {
        setJobReady(false);

        (async () => {
            try {
                const { data } = await endpoints.clients();
                setClients(data || []);
            } catch {
                setClients(await getAll(STORES.clients));
            }
        })();

        (async () => {
            try {
                const { data } = await endpoints.franchiseSettings();
                setFranchiseHQ(data || null);
            } catch {
                setFranchiseHQ(null);
            }
        })();

        (async () => {
            try {
                const { data } = await endpoints.vendorLeaderboard();
                const m = {};
                (data?.vendors || []).forEach((v) => { if (v.variance_pct != null) m[v.item] = v; });
                setVendorStats(m);
            } catch { /* optional */ }
        })();

        (async () => {
            try {
                const { data } = await endpoints.calibrationBands();
                setCalibBands(data?.bands || null);
            } catch { /* optional — engine falls back to the schedule band */ }
        })();

        if (editingJobId) {
            (async () => {
                // Skip the reload when we just navigated here from our own first
                // save — the in-memory inputs are already the latest and a refetch
                // would clobber edits made between save and this effect firing.
                if (editingJobId === justSavedRef.current) { setJobReady(true); return; }
                let job = null;
                try {
                    const { data } = await endpoints.jobs();
                    job = (data || []).find((j) => j.id === editingJobId);
                } catch {
                    job = await getOne(STORES.jobs, editingJobId);
                }
                if (job) {
                    loadedJobRef.current = job;
                    didResyncRef.current = false;
                    setRef(job.ref);
                    setInputs({ ...BLANK, ...(job.inputs || {}) });
                    setSavedRef(job.id);
                    setApproval(job.approval || null);
                    setChangeRequest(job.change_request || null);
                    lastSavedRef.current = JSON.stringify({
                        ref: job.ref,
                        inputs: { ...BLANK, ...(job.inputs || {}) },
                        tm: (job.inputs || {}).mode === "tm" ? ((job.inputs || {}).tm || buildEmptyTM()) : null,
                    });
                }
                // Resolve a linked project for the "Back to project" button.
                if (projectParam) {
                    setProjectLink(projectParam);
                } else {
                    try {
                        const { data: projs } = await endpoints.projects();
                        const linked = (projs || []).find((p) => p.rom_job_id === editingJobId);
                        if (linked) setProjectLink(linked.id);
                    } catch { /* optional */ }
                }
                setJobReady(true);
            })();
        } else {
            // No job → fresh blank. Entry point may pre-set the scale:
            // ?scale=residential ("New Estimate") vs ?scale=commercial ("New ROM").
            const init = { ...BLANK };
            if (scaleParam === "residential") {
                init.propertyScale = "residential";
                init.propertyType = "Single Family Residential";
            } else if (scaleParam === "commercial") {
                init.propertyScale = "commercial";
            }
            setInputs(init);
            setShowRooms(scaleParam === "residential");
            setSavedRef(null);
            setApproval(null);
            setChangeRequest(null);
            setRef(makeRef());
            setSaveMsg("");
            setJobReady(true); // synchronous — no network wait, mic/chat usable immediately
        }
    }, [editingJobId, scaleParam]);

    function startNewROM() {
        // Force a hard reset to a brand-new blank ROM.
        setInputs(BLANK);
        setSavedRef(null);
        setRef(makeRef());
        setSaveMsg("");
        navigate("/calculator/new", { replace: true });
    }

    const result = useMemo(() => {
        if (!schedule) return null;
        if (!hasMinimumInputs(inputs)) return null;
        return computeROM(inputs, schedule, calibBands ? { bands: calibBands } : null);
    }, [inputs, schedule, calibBands]);

    /* Surface the active ROM's real confidence band in the global footer so it
     * agrees with the TotalCard (calibrated bands differ from the ±15% default). */
    const isRom = inputs.mode !== "tm" && inputs.mode !== "cleaning";
    useEffect(() => {
        if (isRom && result) setFooter({ pct: result.confidence_pct, calibrated: result.band_source === "calibrated" });
        else setFooter(null);
        return () => setFooter(null);
    }, [result, isRom, setFooter]);

    const setField = (k, v) => setInputs((p) => ({ ...p, [k]: v }));
    const setTM = (key, value) => setInputs((p) => ({
        ...p,
        tm: { ...(p.tm || buildEmptyTM()), [key]: value },
    }));

    /* ---- Optional room-by-room sq-ft build-up ---- */
    const rooms = inputs.rooms || [];
    const roomTotal = rooms.reduce((s, r) => s + (Number(r.sqft) || 0), 0);
    const sqftOverride = !!inputs.sqftOverride;
    const [showRooms, setShowRooms] = useState(false);
    useEffect(() => { if ((inputs.rooms || []).length > 0) setShowRooms(true); }, [inputs.rooms]);

    const applyRooms = (next) => setInputs((p) => {
        const total = next.reduce((s, r) => s + (Number(r.sqft) || 0), 0);
        const patch = { ...p, rooms: next };
        if (!p.sqftOverride && next.length > 0) patch.sqft = total || "";
        return patch;
    });
    const addRoom = () => { applyRooms([...(inputs.rooms || []), { id: crypto.randomUUID(), name: "", floor: "", sqft: "", notes: "", condition: "", demoLevel: "", materials: "", wallScope: "" }]); setShowRooms(true); };
    const updateRoom = (id, key, val) => applyRooms((inputs.rooms || []).map((r) => (r.id === id ? { ...r, [key]: val } : r)));
    const removeRoom = (id) => applyRooms((inputs.rooms || []).filter((r) => r.id !== id));
    const useRoomTotal = () => setInputs((p) => ({ ...p, sqftOverride: false, sqft: (p.rooms || []).reduce((s, r) => s + (Number(r.sqft) || 0), 0) || "" }));
    const sqftDiffersFromRooms = rooms.length > 0 && sqftOverride && Number(inputs.sqft || 0) !== roomTotal;

    /* ---- Optional site conditions (access/logistics/schedule constraints) ---- */
    const siteConditions = inputs.siteConditions || [];
    const [showSiteConditions, setShowSiteConditions] = useState(false);
    useEffect(() => { if ((inputs.siteConditions || []).length > 0) setShowSiteConditions(true); }, [inputs.siteConditions]);
    const addSiteCondition = () => { setField("siteConditions", [...siteConditions, { text: "", type: "other" }]); setShowSiteConditions(true); };
    const updateSiteCondition = (idx, key, val) => setField("siteConditions", siteConditions.map((c, i) => {
        if (i !== idx) return c;
        const base = typeof c === "string" ? { text: c, type: "other" } : (c || {});
        return { ...base, [key]: val };
    }));
    const removeSiteCondition = (idx) => setField("siteConditions", siteConditions.filter((_, i) => i !== idx));

    /* ---- T&M mode state ---- */
    const tm = inputs.tm || buildEmptyTM();
    const tmTotals = useMemo(() => computeTM(tm), [tm]);
    const isTM = inputs.mode === "tm";
    const isAI = inputs.mode === "ai";
    const isCleaning = inputs.mode === "cleaning";
    const propertyScale = inputs.propertyType ? scaleFromPropertyType(inputs.propertyType) : (inputs.propertyScale || "commercial");
    const isResidential = propertyScale === "residential";
    const docNoun = isCleaning ? "Cleaning" : (isResidential ? "Estimate" : "ROM");
    // Item 9 — property type must be picked before the mode set narrows. Blank
    // jobs default to the commercial mode set until the estimator explicitly
    // chooses, so this flag drives a one-time nudge banner, not a hard block.
    const scaleChosen = !!inputs.propertyType || inputs.propertyScale === "residential" || inputs.propertyScale === "commercial";

    /* ---- Cleaning mode state ---- */
    const cleaning = inputs.cleaning || buildEmptyCleaning();
    const cleaningTotals = useMemo(() => computeCleaning(cleaning, schedule), [cleaning, schedule]);
    const canSaveCleaning = cleaningTotals.grand_total > 0;
    const setCleaning = (patch) => setInputs((p) => ({ ...p, cleaning: { ...(p.cleaning || buildEmptyCleaning()), ...patch } }));
    const [openModal, setOpenModal] = useState(null); // null|labour|consumables|equipment|subs|misc|staffing
    const [clientInlineOpen, setClientInlineOpen] = useState(null); // null | "customer" | "insurer"
    const [showInvoiceImport, setShowInvoiceImport] = useState(false);
    const [showVariance, setShowVariance] = useState(false);
    const [jobRecordOpen, setJobRecordOpen] = useState(false);
    const [actionsOpen, setActionsOpen] = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [sendModal, setSendModal] = useState(null); // null | { type }
    const [creatingProject, setCreatingProject] = useState(false);
    const lastSavedRef = useRef(null);
    const justSavedRef = useRef(null);
    const canSaveTM = tmTotals.grandTotal > 0;

    async function save() {
        if (isTM) {
            if (!canSaveTM) return;
        } else if (isCleaning) {
            if (!canSaveCleaning) return;
        } else {
            if (!result || !schedule) return;
            // Job record (client/insurer/claim) is for the file, not a gate on the
            // estimate — if it's empty & collapsed, reveal it rather than block save.
            if (!inputs.clientId && !inputs.insurerId && !inputs.claimNumber && !jobRecordOpen) {
                setJobRecordOpen(true);
            }
        }
        const isNew = !savedRef;
        // Cleaning is commercial-only by business rule; otherwise derive from property type.
        const propertyScale = isCleaning ? "commercial" : (inputs.propertyType ? scaleFromPropertyType(inputs.propertyType) : (inputs.propertyScale || "commercial"));
        // Raw coefficient-model output (engine prediction) — strip every user
        // override so the three-point store can separate engine vs estimator.
        let enginePrediction = null;
        if (!isTM && !isCleaning && hasMinimumInputs(inputs)) {
            const clean = { ...inputs };
            ["labour_overrides", "labour_extras", "equipment_overrides", "equipment_extras",
             "subcontractors", "misc_overrides", "misc_extras", "timeline_overrides",
             "timeline_extras"].forEach((k) => delete clean[k]);
            const raw = computeROM(clean, schedule);
            enginePrediction = {
                grand_total: raw.grand_total,
                labour_total: raw.labour_total,
                supervision_total: raw.supervision_total,
                field_labour_total: raw.field_labour_total,
                consumables_total: raw.consumables_total,
                equipment_total: raw.equipment_total,
                subs_total: raw.subs_total,
                incidentals_total: raw.incidentals_total,
            };
        }
        const job = {
            id: savedRef || crypto.randomUUID(),
            ref,
            client_id: inputs.clientId || null,
            insurer_id: inputs.insurerId || null,
            claim_number: inputs.claimNumber || "",
            loss_type: inputs.lossType || "",
            property_scale: propertyScale,
            address: inputs.address || "",
            status: "draft",
            inputs,
            result: isTM ? { mode: "tm", ...tmTotals } : (isCleaning ? cleaningTotals : result),
            engine_prediction: enginePrediction,
            created_by: user?.id,
            updated_at: new Date().toISOString(),
        };
        await put(STORES.jobs, job);
        try {
            await endpoints.saveJob(job);
            setSaveMsg("Saved · synced");
        } catch {
            await queueSync("jobs", job);
            setSaveMsg("Saved locally · will sync");
        }
        setSavedRef(job.id);
        justSavedRef.current = job.id;
        lastSavedRef.current = JSON.stringify({ ref, inputs, tm: isTM ? tm : null });
        if (isNew) navigate(`/calculator/${job.id}`, { replace: true });
        setTimeout(() => setSaveMsg(""), 3500);
        return job.id;
    }

    /* ---- Auto-save (debounced) ----
     * Persists the ROM as the estimator edits — no need to click Save. Skips the
     * first render after a load (snapshot matches) and any time there's nothing
     * computable yet, so we never churn blank jobs. */
    useEffect(() => {
        if (!schedule) return;
        const hasContent = isTM ? canSaveTM : isCleaning ? canSaveCleaning : !!result;
        if (!hasContent) return;
        const snapshot = JSON.stringify({ ref, inputs, tm: isTM ? tm : null });
        if (snapshot === lastSavedRef.current) return;
        const t = setTimeout(() => { save(); }, 1500);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputs, tm, result, schedule]);

    /* ---- Re-sync stale stored result ----
     * The engine recomputes the ROM from `inputs` on load; if a job's *stored*
     * result.grand_total drifted from that (e.g. a rate-schedule change, or seed
     * data), persist the freshly computed result once — preserving the job's
     * status/approval — so the calculator, project card and dashboard all agree. */
    useEffect(() => {
        if (isTM || isCleaning) return;
        const job = loadedJobRef.current;
        if (!job || didResyncRef.current || !result || !schedule) return;
        const stored = (job.result || {}).grand_total;
        if (stored == null) return;
        if (Math.abs((result.grand_total || 0) - Number(stored)) < 1) return;
        didResyncRef.current = true;
        const updated = { ...job, result, updated_at: new Date().toISOString() };
        (async () => {
            try { await endpoints.saveJob(updated); await put(STORES.jobs, updated); }
            catch { await queueSync("jobs", updated); }
            loadedJobRef.current = updated;
            lastSavedRef.current = JSON.stringify({
                ref: job.ref, inputs: { ...BLANK, ...(job.inputs || {}) },
                tm: (job.inputs || {}).mode === "tm" ? ((job.inputs || {}).tm || buildEmptyTM()) : null,
            });
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result, schedule, isTM, isCleaning]);

    async function exportPDF() {
        const hasContent = isTM ? canSaveTM : isCleaning ? canSaveCleaning : !!result;
        if (!hasContent || !pdfRef.current) { window.print(); return; }
        try {
            setExporting(true);
            const { exportNodeToPDF } = await import("../lib/pdf");
            await exportNodeToPDF(pdfRef.current, `${isCleaning ? "Quote" : isResidential ? "Estimate" : "ROM"}-${ref}.pdf`);
        } catch {
            window.print(); // fallback to browser print-to-PDF
        } finally {
            setExporting(false);
        }
    }

    /* Populate the granular T&M line-item tracker from the current ROM. Called
     * automatically the first time the user switches to T&M mode (when empty) so
     * the estimate carries across modes without an explicit "convert" step. */
    function populateTMFromROM() {
        if (!result) return;
        const otMult = Number(schedule?.conditions?.ot_multiplier) || 1.5;
        const days = result.days || 1;
        const bi = result.breakdown_inputs || {};

        const labour = (result.labour_lines || [])
            .filter((l) => !l.auto && Number(l.headcount) > 0)
            .map((l) => {
                const hc = Number(l.headcount) || 1;
                const reg = Math.max(0, (Number(l.hours) || 0) - (Number(l.ot_hours) || 0));
                const denom = hc * days;
                return {
                    role: l.role,
                    qty: hc,
                    billingRate: Number(l.rate) || 0,
                    hours: denom ? Number((reg / denom).toFixed(2)) : 0,
                    otRate: Number(((Number(l.rate) || 0) * otMult).toFixed(2)),
                    otHours: denom ? Number(((Number(l.ot_hours) || 0) / denom).toFixed(2)) : 0,
                    days,
                };
            });

        const equipment = (result.equipment_lines || []).map((l) => {
            const qty = Number(l.qty) || 0;
            const d = Number(l.days) || 0;
            const dr = (qty && d) ? Number((Number(l.subtotal) / (qty * d)).toFixed(2)) : (Number(l.rate) || 0);
            return { item: l.item, qty, days: d, dailyRate: dr };
        });

        const subcontractors = (result.subcontractor_lines || []).map((l) => ({
            item: l.item, qty: Number(l.qty) || 0, dailyRate: Number(l.rate) || 0,
            days: Number(l.days) || 1, markupPct: Number(l.markupPct ?? 0.20),
        }));

        const miscellaneous = [];
        if (bi.per_diem_total) miscellaneous.push({ kind: "Per Diem", unitLabel: "Lump (from ROM)", qty: 1, rate: bi.per_diem_total });
        if (bi.mobilisation) miscellaneous.push({ kind: "Mobilization", unitLabel: "Lump (from ROM)", qty: 1, rate: bi.mobilisation });
        if (bi.disposal) miscellaneous.push({ kind: "Misc.", unitLabel: "Disposal (from ROM)", qty: 1, rate: bi.disposal });
        if (bi.permits) miscellaneous.push({ kind: "Misc.", unitLabel: "Permits (from ROM)", qty: 1, rate: bi.permits });
        if (bi.misc_extras_total) miscellaneous.push({ kind: "Misc.", unitLabel: "Other (from ROM)", qty: 1, rate: bi.misc_extras_total });

        setInputs((p) => ({
            ...p,
            mode: "tm",
            romBaseline: result.grand_total || 0,
            tm: {
                labour,
                consumablesPct: Number(p.consumablesPct ?? 0.095),
                equipment,
                subcontractors,
                miscellaneous: miscellaneous.length ? miscellaneous : buildEmptyTM().miscellaneous,
            },
        }));
    }

    /* Switch estimate mode. AI / Sq-ft ROM / T&M all live on the same record and
     * are freely editable. Moving to T&M for the first time seeds its line items
     * from the ROM so nothing is lost across modes. */
    function switchMode(m) {
        if (m === "ai" && !online) return; // AI Estimate needs a live connection.
        if (m === "tm" && !isTM) {
            const tmEmpty = !((tm.labour || []).length || (tm.equipment || []).length || (tm.subcontractors || []).length);
            if (tmEmpty && result) { populateTMFromROM(); return; }
        }
        setField("mode", m);
    }

    const hasContent = isTM ? canSaveTM : isCleaning ? canSaveCleaning : !!result;

    async function openSend(type) {
        setActionsOpen(false);
        let id = savedRef;
        if (!id) id = await save();
        if (id) setSendModal({ type, jobId: id });
    }

    async function createProjectFromROM() {
        if (creatingProject) return;
        setActionsOpen(false);
        setCreatingProject(true);
        try {
            // Flush the latest ROM (incl. recomputed total) so the project
            // picks up the current value, then convert.
            const id = (await save()) || savedRef;
            const { data } = await endpoints.projectFromRom(id, {});
            navigate(`/projects/${data.id}`);
        } catch {
            setSaveMsg("Couldn't create project");
            setTimeout(() => setSaveMsg(""), 3500);
        } finally {
            setCreatingProject(false);
        }
    }

    if (scheduleLoading) {
        return (
            <div className="max-w-[1600px] mx-auto px-6 py-8">
                <div className="rom-card"><div className="rom-card-body">Loading rate schedule…</div></div>
            </div>
        );
    }

    if (!schedule) {
        return (
            <div className="max-w-[1600px] mx-auto px-6 py-8">
                <div className="rom-card">
                    <div className="rom-card-body space-y-3">
                        <div className="font-display font-bold text-base" data-testid="no-schedule-title">
                            No rate schedule cached
                        </div>
                        <p className="text-sm text-[var(--rom-text-2)]">
                            Connect to the internet once to sync before using this offline.
                        </p>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={loadSchedule}
                            data-testid="retry-schedule-btn"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    /* ---- Reusable field blocks (composed differently for ROM vs T&M) ---- */
    const addressBlock = (
        <Field label="Site address" hint="We use this to auto-detect Prime Area rates and Far-Job mileage.">
            <AddressAutocomplete
                value={inputs.address}
                onChange={(v) => setField("address", v)}
                onPick={(parts) => {
                    setField("address", parts.full || parts.address);
                    setField("address_parts", parts);
                    const prime = isPrimeAreaLocation(parts);
                    if (prime !== null) setField("primeArea", prime);
                    if (franchiseHQ?.hq_address_parts?.lat && parts.lat) {
                        const miles = haversineMiles(
                            franchiseHQ.hq_address_parts.lat,
                            franchiseHQ.hq_address_parts.lng,
                            parts.lat,
                            parts.lng,
                        );
                        // Far Job trigger: >100 mi OR out of state — travel still
                        // applies on a short-mileage job that crosses a state line.
                        const outOfState = !!(parts.state && franchiseHQ.hq_address_parts.state
                            && parts.state !== franchiseHQ.hq_address_parts.state);
                        setField("distance_miles", Math.round(miles));
                        setField("outOfState", outOfState);
                        setField("farJob", miles > 100 || outOfState);
                    }
                }}
                placeholder="Start typing… e.g. 1234 Main St"
                testId="job-address"
            />
            {inputs.address_parts?.zip && (
                <div className="text-[11px] text-[var(--rom-text-2)] mt-1 flex items-center gap-2 flex-wrap">
                    <span className="chip chip-green" style={{ padding: "1px 6px" }}>
                        ZIP {inputs.address_parts.zip}
                    </span>
                    <span>{inputs.address_parts.city}, {inputs.address_parts.state}</span>
                    {isPrimeAreaLocation(inputs.address_parts) === true && (
                        <span className="chip chip-orange" style={{ padding: "1px 6px" }}>
                            Prime area auto-detected
                        </span>
                    )}
                    {inputs.distance_miles != null && (
                        <span className={`chip ${inputs.distance_miles > 100 ? "chip-orange" : "chip-navy"}`} style={{ padding: "1px 6px" }} data-testid="distance-chip">
                            {inputs.distance_miles} mi from HQ
                            {inputs.distance_miles > 100 && " · Far Job auto-detected"}
                        </span>
                    )}
                    {inputs.outOfState && (
                        <span className="chip chip-orange" style={{ padding: "1px 6px" }} data-testid="out-of-state-chip">
                            Out of state · Far Job auto-detected
                        </span>
                    )}
                    {!franchiseHQ?.hq_address_parts?.lat && (
                        <span className="text-[var(--rom-text-3)]">· Set HQ address in Settings to enable Far-Job auto-detect</span>
                    )}
                </div>
            )}
        </Field>
    );

    const clientBlock = (
        <Field label="Client">
            <div className="flex gap-2">
                <select className="select flex-1" value={inputs.clientId}
                    onChange={(e) => setField("clientId", e.target.value)}
                    data-testid="job-client">
                    <option value="">— None —</option>
                    {clients.filter(c => c.type === "customer").map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
                <button type="button" onClick={() => setClientInlineOpen("customer")}
                    className="btn btn-ghost btn-sm shrink-0" data-testid="job-client-new" title="Create new client">
                    <Plus size={12} weight="bold" /> New
                </button>
            </div>
        </Field>
    );

    const insurerBlock = (
        <Field label="Insurer">
            <div className="flex gap-2">
                <select className="select flex-1" value={inputs.insurerId}
                    onChange={(e) => setField("insurerId", e.target.value)}
                    data-testid="job-insurer">
                    <option value="">— None —</option>
                    {clients.filter(c => c.type === "insurer").map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
                <button type="button" onClick={() => setClientInlineOpen("insurer")}
                    className="btn btn-ghost btn-sm shrink-0" data-testid="job-insurer-new" title="Create new insurer">
                    <Plus size={12} weight="bold" /> New
                </button>
            </div>
        </Field>
    );

    const claimBlock = (
        <Field label="Claim #">
            <input className="input" value={inputs.claimNumber}
                onChange={(e) => setField("claimNumber", e.target.value)}
                data-testid="job-claim" />
        </Field>
    );

    const propertyTypeBlock = (
        <Field label="Property type">
            <select className="select" value={inputs.propertyType}
                onChange={(e) => setField("propertyType", e.target.value)}
                data-testid="property-type">
                <option value="">— Select property type —</option>
                {["Single Family Residential","Commercial Office","Multifamily Residential","Retail",
                  "Industrial / Warehouse","Medical / Healthcare","Hospitality"].map((p) => (
                    <option key={p}>{p}</option>
                ))}
            </select>
        </Field>
    );

    const primaryLossBlock = (
        <Field label="Primary loss" required={!isTM}>
            <PillGroup
                value={inputs.lossType}
                onChange={(v) => setField("lossType", v)}
                options={[
                    { v: "water", label: "Water" },
                    { v: "fire", label: "Fire/Smoke" },
                    { v: "mould", label: "Mould" },
                    { v: "storm", label: "Storm" },
                ]}
                testId="loss-type"
            />
        </Field>
    );

    const secondaryLossBlock = (
        <Field label="Secondary loss (optional)" hint="For mixed damage — e.g. Water primary + Mould secondary. Improves labour, equipment & plan suggestions.">
            <select className="select" value={inputs.secondaryLossType || ""} onChange={(e) => setField("secondaryLossType", e.target.value)} data-testid="secondary-loss-type">
                <option value="">— None —</option>
                <option value="water">Water</option>
                <option value="fire">Fire</option>
                <option value="mould">Mould</option>
                <option value="storm">Storm</option>
                <option value="smoke">Smoke</option>
                <option value="other">Other</option>
            </select>
            {inputs.secondaryLossType === "other" && (
                <input className="input mt-2 text-sm" placeholder="Describe secondary loss" value={inputs.secondaryLossOther || ""} onChange={(e) => setField("secondaryLossOther", e.target.value)} data-testid="secondary-loss-other" />
            )}
            {inputs.secondaryLossType && inputs.secondaryLossType === inputs.lossType && (
                <div className="text-[11px] mt-1.5 text-[var(--rom-orange-dark)] bg-[var(--rom-orange-soft)]/60 border border-[var(--rom-orange)]/40 rounded px-2 py-1.5" data-testid="secondary-same-warning">Secondary loss matches the primary loss — these usually differ. No extra assumptions will be added.</div>
            )}
        </Field>
    );

    const sqftBlock = (
        <Field label="Total affected sq ft" required={!isTM}>
            <FormulaInput
                value={inputs.sqft}
                placeholder="e.g. 4500 or (50*30)+(20*15)"
                onCommit={(v) => setInputs((p) => ({ ...p, sqft: v, ...((p.rooms || []).length > 0 ? { sqftOverride: true } : {}) }))}
                testId="sqft"
            />
            <div className="flex items-center justify-between mt-1.5 gap-2 flex-wrap">
                <button type="button" onClick={() => setShowRooms((s) => !s)} className="text-[11px] font-semibold text-[var(--rom-navy)] hover:text-[var(--rom-orange)] flex items-center gap-1" data-testid="toggle-room-builder">
                    <Stack size={12} weight="bold" /> {showRooms ? "Hide room-by-room build-up" : "Build room-by-room (optional)"}
                </button>
                {rooms.length > 0 && !sqftOverride && (
                    <span className="text-[11px] text-[var(--rom-text-2)]" data-testid="sqft-room-derived">Auto-totalled from {rooms.length} room{rooms.length === 1 ? "" : "s"}</span>
                )}
            </div>
            {sqftDiffersFromRooms ? (
                <div className="text-[11px] mt-1.5 flex items-center justify-between gap-2 bg-[var(--rom-orange-soft)]/60 border border-[var(--rom-orange)]/40 rounded px-2 py-1.5" data-testid="sqft-override-warning">
                    <span className="text-[var(--rom-orange-dark)] font-medium">Manual total {Number(inputs.sqft || 0).toLocaleString()} sq ft differs from the room build-up ({roomTotal.toLocaleString()} sq ft).</span>
                    <button type="button" onClick={useRoomTotal} className="font-semibold text-[var(--rom-navy)] hover:underline whitespace-nowrap" data-testid="use-room-total">Use room total</button>
                </div>
            ) : (
                <div className="text-[11px] text-[var(--rom-text-2)] mt-1">Excel-style formulas supported — e.g. (5*10)+(6*8). Press Enter or Tab to compute.</div>
            )}
        </Field>
    );

    const roomBuildUpBlock = showRooms ? (
        <RoomBuildUp
            rooms={rooms}
            roomTotal={roomTotal}
            sqftOverride={sqftOverride}
            onAdd={addRoom}
            onUpdate={updateRoom}
            onRemove={removeRoom}
        />
    ) : null;

    const siteConditionsBlock = (
        <div className="mt-2">
            <button type="button" onClick={() => setShowSiteConditions((s) => !s)} className="text-[11px] font-semibold text-[var(--rom-navy)] hover:text-[var(--rom-orange)] flex items-center gap-1" data-testid="toggle-site-conditions">
                {showSiteConditions ? "Hide site conditions" : "Add site conditions (optional)"}
            </button>
            {showSiteConditions && (
                <SiteConditions conditions={siteConditions} onAdd={addSiteCondition} onUpdate={updateSiteCondition} onRemove={removeSiteCondition} />
            )}
        </div>
    );

    const storiesBlock = (
        <Field label="Stories affected">
            <input type="number" min={1} className="input font-mono" placeholder="e.g. 1"
                value={inputs.stories}
                onChange={(e) => setField("stories", e.target.value === "" ? "" : Number(e.target.value))}
                data-testid="stories" />
        </Field>
    );

    const unitCountBlock = (
        <Field label="Discrete units / structures" hint="How many separate units/buildings? >1 sizes parallel crews per unit instead of one crew against total sq ft.">
            <input type="number" min={1} className="input font-mono" placeholder="e.g. 1"
                value={inputs.unitCount ?? 1}
                onChange={(e) => setField("unitCount", e.target.value === "" ? 1 : Math.max(1, Number(e.target.value)))}
                data-testid="unit-count" />
        </Field>
    );

    const waterBlocks = inputs.lossType === "water" ? (
        <>
            <Field label="Water category">
                <PillGroup value={inputs.waterCategory} onChange={(v) => setField("waterCategory", v)}
                    options={[
                        { v: "cat1", label: "Cat 1 — Clean" },
                        { v: "cat2", label: "Cat 2 — Grey" },
                        { v: "cat3", label: "Cat 3 — Black" },
                    ]} testId="water-cat" />
            </Field>
            <Field label="Water class">
                <PillGroup value={inputs.waterClass} onChange={(v) => setField("waterClass", v)}
                    options={[
                        { v: "class1", label: "1" },
                        { v: "class2", label: "2" },
                        { v: "class3", label: "3" },
                        { v: "class4", label: "4" },
                    ]} testId="water-class" />
            </Field>
        </>
    ) : null;

    const scopeBlock = (
        <Field label="Scope scale" required={!isTM} hint="Light = surface clean. Full Gut = demo to substrate + rebuild prep.">
            <PillGroup value={inputs.scope} onChange={(v) => setField("scope", v)}
                options={[
                    { v: "light", label: "Light" },
                    { v: "moderate", label: "Moderate" },
                    { v: "heavy", label: "Heavy" },
                    { v: "full_gut", label: "Full Gut" },
                ]} testId="scope" />
        </Field>
    );

    const hoursBlock = (
        <Field label="Hours since loss">
            <input type="number" min={0} className="input font-mono" placeholder="e.g. 6"
                value={inputs.hoursSinceLoss}
                onChange={(e) => setField("hoursSinceLoss", e.target.value === "" ? "" : Number(e.target.value))}
                data-testid="hours-since-loss" />
        </Field>
    );

    const consumablesBlock = (
        <Field label="Consumables % of labour" hint="Enter any %, including negative (e.g. -5 to net out).">
            <div className="flex items-center gap-2">
                <input type="number" step="0.5"
                    className="input font-mono w-28"
                    value={inputs.consumablesPct == null || inputs.consumablesPct === "" ? "" : Number((inputs.consumablesPct * 100).toFixed(2))}
                    onChange={(e) => setField("consumablesPct", e.target.value === "" ? "" : Number(e.target.value) / 100)}
                    placeholder="5"
                    data-testid="consumables-pct-input" />
                <span className="text-sm text-[var(--rom-text-2)]">%</span>
            </div>
        </Field>
    );

    const otBlock = (
        <Field label="OT hours / day per role">
            <input type="number" min={0} max={6} step={0.5} className="input font-mono"
                value={inputs.otHoursPerDay}
                onChange={(e) => setField("otHoursPerDay", Number(e.target.value))}
                data-testid="ot-hours" />
        </Field>
    );

    const taxBlock = (
        <Field label="Tax rate %" hint="Per-job setting. Added on top of the ROM total — never changes grand_total, which stays pre-tax for calibration.">
            <div className="flex items-center gap-2">
                <input type="number" min={0} step="0.1"
                    className="input font-mono w-28"
                    value={inputs.taxRatePct == null || inputs.taxRatePct === "" ? "" : Number((inputs.taxRatePct * 100).toFixed(2))}
                    onChange={(e) => setField("taxRatePct", e.target.value === "" ? "" : Number(e.target.value) / 100)}
                    placeholder="0"
                    data-testid="tax-rate-pct-input" />
                <span className="text-sm text-[var(--rom-text-2)]">%</span>
            </div>
        </Field>
    );

    const togglesBlock = (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <Toggle label="Reconstruction prep" desc="Mitigation + recon (vs mitigation only)"
                value={inputs.includeRecon} onChange={(v) => setField("includeRecon", v)} testId="toggle-recon" />
            <Toggle label="Prime Area rates" desc="Boston, NYC, CA, WA, OR, AK, HI, declared CAT zones"
                value={inputs.primeArea} onChange={(v) => setField("primeArea", v)} testId="toggle-prime" />
            <Toggle label="Emergency / after-hours start" desc="Adds one-time emergency service fee"
                value={inputs.emergency} onChange={(v) => setField("emergency", v)} testId="toggle-emergency" />
            <Toggle label="Far job (>100mi one-way)"
                desc={inputs.distance_miles != null
                    ? `${inputs.distance_miles} mi from franchise HQ · increases mobilisation budget`
                    : "Increases mobilisation budget"}
                value={inputs.farJob} onChange={(v) => setField("farJob", v)} testId="toggle-farjob" />
        </div>
    );

    /* "Refine estimate" summary — count of optional fields the user has set. */
    const refineFlags = [
        !!inputs.propertyType,
        !!inputs.secondaryLossType,
        inputs.stories !== "" && inputs.stories != null,
        Number(inputs.unitCount) > 1,
        inputs.hoursSinceLoss !== "" && inputs.hoursSinceLoss != null,
        Number(inputs.consumablesPct) !== 0.095,
        Number(inputs.otHoursPerDay) > 0,
        !!inputs.includeRecon,
        !!inputs.primeArea,
        !!inputs.emergency,
        !!inputs.farJob,
    ];
    if (inputs.lossType === "water") refineFlags.push(!!inputs.waterCategory, !!inputs.waterClass);
    const refineSet = refineFlags.filter(Boolean).length;
    const refineSummary = refineSet > 0 ? `${refineSet} of ${refineFlags.length} set` : "all optional";


    return (
        <div className="max-w-[1600px] mx-auto px-6 py-8">
            <header className="flex flex-wrap items-end justify-between gap-4 mb-6 no-print">
                <div>
                    <button
                        onClick={() => navigate(projectLink ? `/projects/${projectLink}` : "/estimates")}
                        className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] hover:text-[var(--rom-text)] font-semibold mb-1 flex items-center gap-1.5"
                        data-testid="back-to-list"
                    >
                        <ArrowLeft size={11} weight="bold" /> {projectLink ? "Back to project" : "All estimates"}
                    </button>
                    <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight flex items-center flex-wrap gap-3">
                        <CalcIcon size={28} weight="bold" className="text-[var(--rom-orange)]" />
                        {docNoun} <span className="font-mono text-base ml-2 text-[var(--rom-text-2)]">{ref}</span>
                        <span className={`chip ${savedRef ? "chip-navy" : "chip-orange"} ml-2`}>
                            {savedRef ? "Editing" : `New ${docNoun}`}
                        </span>
                        {approval && (
                            <span className="chip chip-green ml-1" data-testid="rom-approval-chip" title={approval.by ? `Signed by ${approval.by}${approval.at ? " on " + new Date(approval.at).toLocaleDateString() : ""}` : undefined}>
                                <CheckCircle size={12} weight="fill" /> {({ client: "Approved by Client", insurer: "Approved by Insurance", adjuster: "Approved by Adjuster" })[approval.recipient_type] || "Approved"}
                            </span>
                        )}
                    </h1>
                    <span className="inline-flex items-center gap-1 text-[11px] text-[var(--rom-text-3)] mt-1" data-testid="autosave-status">
                        {saveMsg
                            ? <><CheckCircle size={11} weight="fill" className="text-[var(--rom-green)]" /> {saveMsg}</>
                            : (savedRef ? <><CheckCircle size={11} weight="bold" /> Auto-saved</> : "Edits save automatically")}
                    </span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="inline-flex rounded-[4px] border border-[var(--rom-border-strong)] overflow-hidden" data-testid="mode-toggle">
                        <button
                            onClick={() => switchMode("ai")}
                            disabled={!online}
                            title={!online ? "AI Estimate needs a live connection. Use ROM or T&M to keep working offline." : undefined}
                            className={`px-3 py-2 text-xs font-semibold transition-colors flex items-center gap-1.5 ${isAI ? "bg-[var(--rom-orange)] text-white" : "bg-white text-[var(--rom-text)] hover:bg-slate-50"} disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white`}
                            data-testid="mode-ai"
                        >
                            <Sparkle size={12} weight="fill" /> AI Estimate
                        </button>
                        <button
                            onClick={() => switchMode("rom")}
                            className={`px-3 py-2 text-xs font-semibold transition-colors border-l border-[var(--rom-border-strong)] ${inputs.mode === "rom" ? "bg-[var(--rom-navy)] text-white" : "bg-white text-[var(--rom-text)] hover:bg-slate-50"}`}
                            data-testid="mode-rom"
                        >
                            {isResidential ? "Estimate" : "ROM"}
                        </button>
                    </div>

                    {/* Cleaning is commercial-only by business rule; kept visually separate
                        from the ROM/AI peer group (dashed amber border) rather than a fourth
                        peer tab, and hidden entirely once a Residential property is picked. */}
                    {!isResidential && (
                        <button
                            onClick={() => switchMode("cleaning")}
                            title="Commercial cleaning — flat-rate quote, separate from the restoration estimate"
                            className={`px-3 py-2 text-xs font-semibold rounded-[4px] border-2 border-dashed transition-colors flex items-center gap-1.5 ${isCleaning ? "bg-[var(--rom-warn)] text-white border-[var(--rom-warn)]" : "bg-white text-[var(--rom-warn)] border-[var(--rom-warn)] hover:bg-amber-50"}`}
                            data-testid="mode-cleaning"
                        >
                            Cleaning
                        </button>
                    )}

                    {/* T&M (Excel-style) demoted to a tucked-away "Advanced" affordance —
                        it's a fully manual line-item system that feeds nothing into the
                        calibration loop, so it shouldn't read as a peer estimating mode. */}
                    <div className="relative">
                        <button
                            onClick={() => setAdvancedOpen((o) => !o)}
                            title="Manual override — line items here don't feed the calibration loop"
                            className={`px-2.5 py-2 text-[11px] font-semibold rounded-[4px] border transition-colors flex items-center gap-1 ${isTM ? "bg-[var(--rom-navy)] text-white border-[var(--rom-navy)]" : "bg-white text-[var(--rom-text-3)] border-[var(--rom-border)] hover:text-[var(--rom-text)] hover:bg-slate-50"}`}
                            data-testid="mode-advanced-toggle"
                        >
                            {isTM ? "Advanced: T&M" : "Advanced"} <CaretDown size={10} weight="bold" />
                        </button>
                        {advancedOpen && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setAdvancedOpen(false)} />
                                <div className="absolute right-0 mt-1 w-64 bg-white rounded-[6px] shadow-xl border border-[var(--rom-border)] z-50 overflow-hidden py-1" data-testid="mode-advanced-menu">
                                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-bold">Manual override</div>
                                    <button
                                        onClick={() => { switchMode("tm"); setAdvancedOpen(false); }}
                                        className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-start gap-2"
                                        data-testid="mode-tm"
                                    >
                                        <Stack size={14} weight="bold" className="text-[var(--rom-text-2)] mt-0.5" />
                                        <span>
                                            <span className="block font-semibold">T&amp;M (Excel-style)</span>
                                            <span className="block text-[10px] text-[var(--rom-text-3)]">Manual line-item entry — doesn't feed calibration</span>
                                        </span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                    {savedRef && (
                        <button className="btn btn-ghost !hidden sm:!inline-flex" onClick={() => setShowInvoiceImport(true)} data-testid="calc-upload-invoice" title="Record the final invoice (AI) against this ROM">
                            <FilePdf size={14} weight="bold" className="text-[var(--rom-orange)]" /> Upload invoice
                        </button>
                    )}
                    {editingJobId && (
                        <button className="btn btn-ghost !hidden sm:!inline-flex" onClick={() => setShowVariance(true)} data-testid="calc-variance-btn" title="Variance analysis (ROM vs recorded actual)">
                            <ChartBar size={14} weight="bold" className="text-[var(--rom-orange)]" /> Variance
                        </button>
                    )}
                    {/* Actions — always visible; send/project actions enable once a ROM is computed */}
                    <div className="relative">
                        <button className="btn btn-primary" onClick={() => setActionsOpen((o) => !o)} data-testid="rom-actions-btn">
                            <PaperPlaneTilt size={14} weight="bold" /> Actions <CaretDown size={12} weight="bold" />
                        </button>
                        {actionsOpen && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setActionsOpen(false)} />
                                <div className="absolute left-0 sm:left-auto sm:right-0 mt-1 w-60 bg-white rounded-[6px] shadow-xl border border-[var(--rom-border)] z-50 overflow-hidden py-1" data-testid="rom-actions-menu">
                                    {/* Upload invoice / Variance live here too (mobile-only) so nothing
                                        requires scrolling the header sideways to reach on a phone. */}
                                    {(savedRef || editingJobId) && (
                                        <div className="sm:hidden">
                                            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-bold">More</div>
                                            {savedRef && <ActionItem icon={<FilePdf size={15} weight="bold" className="text-[var(--rom-orange)]" />} label="Upload invoice" onClick={() => { setActionsOpen(false); setShowInvoiceImport(true); }} testId="action-upload-invoice" />}
                                            {editingJobId && <ActionItem icon={<ChartBar size={15} weight="bold" className="text-[var(--rom-orange)]" />} label="Variance analysis" onClick={() => { setActionsOpen(false); setShowVariance(true); }} testId="action-variance" />}
                                            <div className="border-t border-[var(--rom-border)] my-1" />
                                        </div>
                                    )}
                                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-bold">Send proposal</div>
                                    <ActionItem icon={<PaperPlaneTilt size={15} weight="bold" />} label="Send to Adjuster" onClick={() => openSend("adjuster")} disabled={!hasContent} testId="action-send-adjuster" />
                                    <ActionItem icon={<PaperPlaneTilt size={15} weight="bold" />} label="Send to Insurance" onClick={() => openSend("insurer")} disabled={!hasContent} testId="action-send-insurer" />
                                    <ActionItem icon={<PaperPlaneTilt size={15} weight="bold" />} label="Send to Client" onClick={() => openSend("client")} disabled={!hasContent} testId="action-send-client" />
                                    <div className="border-t border-[var(--rom-border)] my-1" />
                                    <ActionItem icon={creatingProject ? <CircleNotch size={15} className="animate-spin" /> : <FolderPlus size={15} weight="bold" className="text-[var(--rom-green)]" />} label="Create new project" onClick={createProjectFromROM} disabled={!hasContent} testId="action-create-project" />
                                    <div className="border-t border-[var(--rom-border)] my-1" />
                                    <ActionItem icon={exporting ? <CircleNotch size={15} className="animate-spin" /> : <Printer size={15} weight="bold" />} label={exporting ? "Generating PDF…" : "Export PDF"} onClick={() => { setActionsOpen(false); exportPDF(); }} disabled={!hasContent || exporting} testId="export-pdf" />
                                    <ActionItem icon={<Plus size={15} weight="bold" />} label="New blank ROM" onClick={() => { setActionsOpen(false); startNewROM(); }} testId="new-rom-btn" />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </header>

            {/* Inputs-used panel — shows exactly what's driving this ROM, with a
                tag for whether each field was actually set or is sitting on its
                engine default, so nothing is hidden behind the total. */}
            {!isTM && !isCleaning && !!result && (() => {
                const rows = [
                    { k: "Loss type", v: inputs.lossType, set: !!inputs.lossType },
                    { k: "Secondary loss", v: inputs.secondaryLossType || "—", set: !!inputs.secondaryLossType },
                    { k: "Scope", v: inputs.scope, set: !!inputs.scope },
                    { k: "Sq ft", v: inputs.sqft, set: !!inputs.sqft },
                    { k: "Property type", v: inputs.propertyType || "— (defaulted commercial)", set: !!inputs.propertyType },
                    { k: "Units", v: inputs.unitCount ?? 1, set: Number(inputs.unitCount) > 1 },
                    ...(inputs.lossType === "water" ? [
                        { k: "Water category", v: inputs.waterCategory || "— (defaults to Cat 1)", set: !!inputs.waterCategory },
                        { k: "Water class", v: inputs.waterClass || "— (defaults to Class 2)", set: !!inputs.waterClass },
                    ] : []),
                    { k: "Hours since loss", v: inputs.hoursSinceLoss === "" || inputs.hoursSinceLoss == null ? "—" : inputs.hoursSinceLoss, set: inputs.hoursSinceLoss !== "" && inputs.hoursSinceLoss != null },
                    { k: "Consumables %", v: `${((Number(inputs.consumablesPct) || 0) * 100).toFixed(1)}%`, set: Number(inputs.consumablesPct) !== 0.095 && inputs.consumablesPct != null },
                    { k: "OT hrs/day", v: inputs.otHoursPerDay || 0, set: Number(inputs.otHoursPerDay) > 0 },
                    { k: "Far job", v: inputs.farJob ? "Yes" : "No", set: !!inputs.farJob },
                    { k: "Prime area", v: inputs.primeArea ? "Yes" : "No", set: !!inputs.primeArea },
                    { k: "Emergency start", v: inputs.emergency ? "Yes" : "No", set: !!inputs.emergency },
                ];
                const flags = (result.assumptions || []).filter((a) => a.unvalidated);
                return (
                    <details className="rounded-[4px] border border-[var(--rom-border)] bg-white px-4 py-2.5 mb-4 no-print group" data-testid="inputs-used-panel">
                        <summary className="flex items-center justify-between cursor-pointer list-none text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--rom-text-2)] hover:text-[var(--rom-text)]">
                            <span className="flex items-center gap-2">
                                Inputs used
                                <span className={`chip ${isAI ? "chip-orange" : "chip-navy"}`} style={{ padding: "1px 6px" }} data-testid="inputs-used-source">
                                    {isAI ? "AI Estimate" : "Manual entry"}
                                </span>
                            </span>
                            <span className="font-mono tabular text-[var(--rom-orange)] group-open:rotate-180 transition-transform">▾</span>
                        </summary>
                        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2.5">
                            {rows.map((r) => (
                                <div key={r.k} className="text-xs" data-testid={`inputs-used-${r.k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                    <div className="text-[var(--rom-text-3)]">{r.k}</div>
                                    <div className="font-semibold text-[var(--rom-text)] flex items-center gap-1.5">
                                        {String(r.v)}
                                        {!r.set && <span className="text-[9px] font-normal uppercase tracking-wide text-[var(--rom-text-3)] bg-slate-100 rounded px-1">default</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {flags.length > 0 && (
                            <div className="mt-3 pt-2.5 border-t border-[var(--rom-border)]" data-testid="assumption-flags">
                                <div className="text-[10px] uppercase tracking-wide text-[var(--rom-text-3)] mb-1.5">Assumption flags · internal only</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {flags.map((a) => (
                                        <span
                                            key={a.key}
                                            className="chip"
                                            style={{ padding: "2px 7px", background: "#fef3c7", color: "#92400e" }}
                                            title={a.note}
                                            data-testid={`assumption-flag-${a.key}`}
                                        >
                                            {a.label}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </details>
                );
            })()}

            {/* Item 9 — property type picked first, narrows the mode set below.
                Non-blocking nudge (blank jobs default to the commercial set), but
                gets the estimator into the correct Residential/Commercial track
                before they start picking a mode. */}
            {!scaleChosen && !isTM && !isCleaning && (
                <div className="rounded-[4px] border border-[var(--rom-border-strong)] bg-white px-4 py-3 mb-4 flex flex-wrap items-center gap-3 no-print" data-testid="scale-gate-banner">
                    <span className="text-xs font-semibold text-[var(--rom-text-2)]">What kind of property is this?</span>
                    <button
                        onClick={() => { setField("propertyType", "Single Family Residential"); setField("propertyScale", "residential"); }}
                        className="btn btn-ghost text-xs"
                        data-testid="scale-gate-residential"
                    >
                        <House size={13} weight="bold" /> Residential
                    </button>
                    <button
                        onClick={() => setField("propertyScale", "commercial")}
                        className="btn btn-ghost text-xs"
                        data-testid="scale-gate-commercial"
                    >
                        <Buildings size={13} weight="bold" /> Commercial
                    </button>
                    <span className="text-[11px] text-[var(--rom-text-3)]">Narrows the estimate modes above to what's relevant.</span>
                </div>
            )}

            {isTM && Number(inputs.romBaseline) > 0 && (() => {
                const romT = Number(inputs.romBaseline);
                const tmT = tmTotals.grandTotal;
                const delta = romT > 0 ? ((tmT - romT) / romT) * 100 : 0;
                const over = delta > 0;
                return (
                    <div
                        className="rounded-[4px] bg-[var(--rom-navy)] text-white px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-1 mb-4 no-print"
                        data-testid="reconciliation-banner"
                    >
                        <span className="text-[11px] uppercase tracking-[0.18em] text-white/60 font-semibold">ROM → T&amp;M reconciliation</span>
                        <span className="text-sm">ROM was <b className="font-mono">{formatCurrency(romT)}</b></span>
                        <ArrowsLeftRight size={14} weight="bold" className="text-white/50" />
                        <span className="text-sm">T&amp;M is <b className="font-mono">{formatCurrency(tmT)}</b></span>
                        <span className={`text-sm font-bold font-mono ${over ? "text-[var(--rom-orange)]" : "text-emerald-300"}`}>
                            {over ? "▲ +" : "▼ "}{delta.toFixed(1)}%
                        </span>
                        <span className="text-[11px] text-white/55">
                            {Math.abs(delta) <= 15 ? "Within the ±15% ROM band" : "Outside the ±15% ROM band — review scope"}
                        </span>
                        <button
                            onClick={() => setField("romBaseline", 0)}
                            className="ml-auto text-white/60 hover:text-white"
                            title="Dismiss"
                            data-testid="reconciliation-dismiss"
                        >
                            <X size={14} weight="bold" />
                        </button>
                    </div>
                );
            })()}

            {isTM && (
                <div
                    className="rounded-[4px] bg-blue-50 border-l-4 border-blue-400 p-3 text-xs text-blue-900 no-print"
                    data-testid="tm-required-banner"
                >
                    <b>Time &amp; Material mode:</b> no fields are technically required — open any of the 5 schedules on the right and start adding line items. Save unlocks once the grand total &gt; $0.
                </div>
            )}

            {changeRequest && (changeRequest.message || changeRequest.items?.length > 0) && (
                <div className="rounded-[4px] border-l-4 border-[var(--rom-orange)] bg-[var(--rom-orange-soft)]/50 p-4 no-print" data-testid="rom-change-request-banner">
                    <div className="flex items-start gap-2">
                        <ChatCircleText size={18} weight="bold" className="text-[var(--rom-orange-dark)] mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm text-[var(--rom-text)]">
                                Changes requested{changeRequest.requested_by ? ` by ${changeRequest.requested_by}` : ""}
                            </div>
                            {changeRequest.items?.length > 0 && (
                                <ul className="mt-1.5 space-y-1">
                                    {changeRequest.items.map((it, i) => (
                                        <li key={i} className="text-xs text-[var(--rom-text)] flex flex-wrap items-baseline gap-x-1.5" data-testid={`rom-change-item-${i}`}>
                                            <span className="chip chip-orange" style={{ padding: "0px 6px" }}>{it.tag}</span>
                                            <b>{it.label}</b>
                                            {it.suggested_amount != null && <span className="font-mono">→ suggests {formatCurrency(it.suggested_amount)}</span>}
                                            {it.comment && <span className="italic text-[var(--rom-text-2)]">“{it.comment}”</span>}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {changeRequest.message && <div className="text-xs text-[var(--rom-text-2)] mt-1.5"><b>Note:</b> {changeRequest.message}</div>}
                            <div className="text-[11px] text-[var(--rom-text-3)] mt-1.5">Make your edits below, then re-send the proposal to clear this.</div>
                        </div>
                        <button className="text-[var(--rom-text-3)] hover:text-[var(--rom-text)] shrink-0" onClick={() => setChangeRequest(null)} title="Dismiss" data-testid="rom-change-dismiss">
                            <X size={16} weight="bold" />
                        </button>
                    </div>
                </div>
            )}

            <div className={isAI ? "" : "hidden"}>
                <AIEstimator
                    active={isAI}
                    schedule={schedule}
                    vendorStats={vendorStats}
                    calibBands={calibBands}
                    franchiseHQ={franchiseHQ}
                    refLabel={ref}
                    editingJobId={editingJobId}
                    jobReady={jobReady}
                    savedInputs={inputs}
                    onInputsChange={(ai) => setInputs((p) => (p.mode === "ai" ? { ...p, ...ai, mode: "ai" } : p))}
                    onOpenInEditor={(ai) => setInputs({ ...BLANK, ...ai, mode: ai.mode === "cleaning" ? "cleaning" : "rom" })}
                />
            </div>
            {!isAI && (isCleaning ? (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className="lg:col-span-8 space-y-6 no-print">
                    <div className="rounded-[4px] bg-[var(--rom-orange-soft)]/60 border-l-4 border-[var(--rom-orange)] p-3 text-xs text-[var(--rom-text)] no-print" data-testid="cleaning-mode-banner">
                        <b>Commercial Cleaning mode:</b> standalone clean / sanitize quote — flat unit-rate pricing from Schedule F. No loss type, scope or insurance reserve language. Commercial only.
                    </div>
                    <Section tag="1" title="Job details">
                        <div className="space-y-4">
                            {addressBlock}
                            <Field label="Facility type" hint="Informational only — does not affect pricing.">
                                <input className="input" placeholder="e.g. Warehouse, Office, Medical clinic"
                                    value={cleaning.facilityType || ""} onChange={(e) => setCleaning({ facilityType: e.target.value })}
                                    data-testid="cleaning-facility-type" />
                            </Field>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {clientBlock}
                                {insurerBlock}
                            </div>
                        </div>
                    </Section>
                    <CleaningPanel cleaning={cleaning} schedule={schedule} onChange={setCleaning} />
                </div>
                <div className="lg:col-span-4">
                    <div className="lg:sticky lg:top-4 space-y-4">
                        <CleaningTotalCard result={cleaningTotals} />
                    </div>
                </div>
                <div className="hidden print:block print-area col-span-12">
                    <PrintROM ref_={ref} inputs={inputs} result={null} schedule={schedule} clients={clients} />
                </div>
            </div>
            ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* LEFT — Inputs */}
                <div className="lg:col-span-8 space-y-6 no-print">
                    {isTM ? (
                        <>
                            <Section tag="1" title="Job details">
                                <div className="space-y-4">
                                    {addressBlock}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {clientBlock}
                                        {insurerBlock}
                                        {claimBlock}
                                    </div>
                                </div>
                            </Section>
                            <Section tag="2" title="Loss & property">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {propertyTypeBlock}
                                    {primaryLossBlock}
                                    {secondaryLossBlock}
                                    {sqftBlock}
                                    {storiesBlock}
                                    {unitCountBlock}
                                    {waterBlocks}
                                </div>
                                {roomBuildUpBlock}
                                {siteConditionsBlock}
                            </Section>
                            <Section tag="3" title="Scope & conditions">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {scopeBlock}
                                    {hoursBlock}
                                    {consumablesBlock}
                                    {otBlock}
                                    {taxBlock}
                                </div>
                                {togglesBlock}
                            </Section>
                        </>
                    ) : (
                        <>
                            <Section tag="1" title={isResidential ? "Quick estimate" : "Quick ROM"}>
                                <div className="space-y-4">
                                    {addressBlock}
                                    <RateBasisBanner inputs={inputs} setField={setField} />
                                    {primaryLossBlock}
                                    {sqftBlock}
                                    {roomBuildUpBlock}
                                    {siteConditionsBlock}
                                    {scopeBlock}
                                </div>
                            </Section>
                            <CollapsibleSection tag="2" title={isResidential ? "Advanced (optional)" : "Refine estimate"} summary={refineSummary}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {propertyTypeBlock}
                                    {secondaryLossBlock}
                                    {storiesBlock}
                                    {!isResidential && unitCountBlock}
                                    {waterBlocks}
                                    {hoursBlock}
                                    {consumablesBlock}
                                    {otBlock}
                                    {taxBlock}
                                </div>
                                {togglesBlock}
                            </CollapsibleSection>
                            <CollapsibleSection
                                tag="3"
                                title="Job record"
                                summary={(() => { const n = [inputs.clientId, inputs.insurerId, inputs.claimNumber].filter(Boolean).length; return n ? `${n} set` : "optional"; })()}
                                open={jobRecordOpen}
                                onOpenChange={setJobRecordOpen}
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {clientBlock}
                                    {insurerBlock}
                                    {claimBlock}
                                </div>
                            </CollapsibleSection>
                        </>
                    )}
                    {!isTM && result && <DeliverablesBlock result={result} inputs={inputs} schedule={schedule} setField={setField} vendorStats={vendorStats} />}
                </div>

                {/* RIGHT — Sticky total */}
                <div className="lg:col-span-4">
                    <div className="lg:sticky lg:top-4 space-y-4">
                        {isTM
                            ? <CostTracker
                                tm={tm}
                                onOpen={(k) => setOpenModal(k)}
                                onShowStaffing={() => setOpenModal("staffing")}
                              />
                            : (result
                                ? <TotalCard result={result} inputs={inputs} ref_={ref} />
                                : <EmptyTotalCard inputs={inputs} />)}
                    </div>
                </div>

                {/* PRINT-ONLY VERSION */}
                <div className="hidden print:block print-area col-span-12">
                    <PrintROM ref_={ref} inputs={inputs} result={result} schedule={schedule} clients={clients} />
                </div>
            </div>
            ))}

            {/* OFF-SCREEN render target for native PDF export (html2canvas) */}
            <div
                ref={pdfRef}
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", top: 0, width: "800px", background: "#fff", padding: "28px" }}
            >
                <PrintROM ref_={ref} inputs={inputs} result={result} schedule={schedule} clients={clients} />
            </div>

            {/* Inline +Create Client / Insurer */}
            <ClientInlineModal
                open={!!clientInlineOpen}
                type={clientInlineOpen || "customer"}
                onClose={() => setClientInlineOpen(null)}
                onCreated={(c) => {
                    setClients((list) => {
                        const without = list.filter((x) => x.id !== c.id);
                        return [...without, c].sort((a, b) => a.name.localeCompare(b.name));
                    });
                    if (c.type === "insurer") setField("insurerId", c.id);
                    else setField("clientId", c.id);
                }}
            />

            {savedRef && (
                <InvoiceImportModal
                    open={showInvoiceImport}
                    onClose={() => setShowInvoiceImport(false)}
                    lockedJob={{
                        id: savedRef,
                        ref,
                        project_name: inputs.projectName || ref,
                        grand_total: isTM ? tmTotals.grandTotal : (result?.grand_total || 0),
                    }}
                    onApplied={() => {
                        setSaveMsg("Invoice recorded · actuals saved");
                        navigate("/estimates");
                    }}
                />
            )}
            {/* T&M Drill-in modals */}
            {isTM && (
                <>
                    <LabourModal
                        open={openModal === "labour"}
                        onClose={() => setOpenModal(null)}
                        scheduleA={schedule?.schedule_a || []}
                        usePrime={!!inputs.primeArea}
                        lines={tm.labour}
                        onSave={(lines) => setTM("labour", lines)}
                    />
                    <ConsumablesModal
                        open={openModal === "consumables"}
                        onClose={() => setOpenModal(null)}
                        labourTotal={tmTotals.labour}
                        pct={tm.consumablesPct}
                        onSave={(p) => setTM("consumablesPct", p)}
                    />
                    <EquipmentModal
                        open={openModal === "equipment"}
                        onClose={() => setOpenModal(null)}
                        scheduleC={schedule?.schedule_c || []}
                        lines={tm.equipment}
                        onSave={(lines) => setTM("equipment", lines)}
                    />
                    <SubcontractorsModal
                        open={openModal === "subs"}
                        onClose={() => setOpenModal(null)}
                        lines={tm.subcontractors}
                        onSave={(lines) => setTM("subcontractors", lines)}
                    />
                    <MiscModal
                        open={openModal === "misc"}
                        onClose={() => setOpenModal(null)}
                        lines={tm.miscellaneous}
                        onSave={(lines) => setTM("miscellaneous", lines)}
                    />
                    <StaffingModal
                        open={openModal === "staffing"}
                        onClose={() => setOpenModal(null)}
                        lines={tm.labour}
                    />
                </>
            )}
            <VarianceAnalysis
                jobId={editingJobId}
                open={showVariance}
                onClose={() => setShowVariance(false)}
            />
            <SendProposalModal
                open={!!sendModal}
                onClose={() => setSendModal(null)}
                recipientType={sendModal?.type || "client"}
                clients={clients}
                job={{ id: sendModal?.jobId, ref, inputs, result, grandTotal: isTM ? tmTotals.grandTotal : (result?.grand_total || 0) }}
            />
        </div>
    );
}

function ActionItem({ icon, label, onClick, testId, disabled }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[var(--rom-text)] hover:bg-slate-50 transition-colors text-left disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            data-testid={testId}
        >
            <span className="text-[var(--rom-navy)]">{icon}</span> {label}
        </button>
    );
}

/* ---------------- Sub-components ---------------- */
